import React, { useMemo, useCallback, useState } from 'react';
import {
  Table as TableComponent,
  Button,
  Dropdown,
  Box,
  Typography,
  StatusBanner,
} from '@lucid/lego';
import type { FilterState } from '@lucid/lego';
import { useUserIdentity } from '@lucid/portal-core';
import type {
  Prospect,
  ExportProspectsResponse,
  ProspectSearchFilters,
} from '../../../../api/services/lupp/prospect/dto.prospect';
import {
  useExportProspects,
  useGetProspectsExportHistory,
  useGetProspectListMetadata,
} from '../../../../api/services/lupp/prospect/prospect';
import { downloadFileFromFilehub } from '../../../../api/services/lupp/fileUpload/fileUpload';
import {
  PROSPECTS_EXPORT_TYPE_ALL,
  PROSPECTS_EXPORT_TYPE_INCREMENTAL,
  PROSPECTS_EXPORT_TYPE_SEARCH,
  EXPORT_MODE_INCREMENTAL,
  EXPORT_MODE_ALL,
  EXPORT_MODE_SEARCH,
  type ExportHistoryLog,
} from '../../constants';
import { FILEHUB_MODULE_NAME } from '../../../../constants/constants';
import { useStore } from '../../../../store/store';
import { isHistoryExportEnabledForAllRoles } from '../../../../config/featureFlags';
import {
  createColumns,
  toMetadataFilterOptions,
  toLocationFilterOptions,
  buildFilterState,
  parseFilterState,
} from './utils';
import Details from '../Details/Details';
import ExportHistory from './ExportHistory/ExportHistory';
import styles from './utils.module.scss';

type TableProps = {
  theme: 'light' | 'dark';
  data: Prospect[];
  totalCount: number;
  currentPage: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  labels: Record<string, string>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onCreateQuotation: (prospectId?: string) => void;
  onCreateProspect: () => void;
  onViewAllTestDrives: () => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  /** Currently applied search filters (status, location, owner, source, created date range) */
  filters?: ProspectSearchFilters;
  /** Called when the user applies new filters from the Table's filter panel */
  onFiltersChange?: (filters: ProspectSearchFilters) => void;
  selectedProspectId?: string | null;
  onProspectSelect?: (prospectId: string | null) => void;
  /** Active tab ID from URL hash */
  activeTab?: string;
  /** Callback when tab changes */
  onTabChange?: (tabId: string) => void;
  /** Callback when a quote is clicked */
  onQuoteClick?: (quoteId: string) => void;
  /** Locale for date/currency formatting */
  locale?: string;
};

const Table: React.FC<TableProps> = ({
  theme,
  data,
  totalCount,
  currentPage,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  labels,
  searchQuery,
  onSearchChange,
  onCreateQuotation,
  onCreateProspect,
  onViewAllTestDrives,
  sortColumn,
  sortDirection,
  onSort,
  filters = {},
  onFiltersChange,
  selectedProspectId,
  onProspectSelect,
  activeTab = 'details',
  onTabChange,
  onQuoteClick,
  locale = 'en-US',
}) => {
  // Get store actions for managing selected prospect
  const setSelectedProspect = useStore((state) => state.setSelectedProspect);
  const clearSelectedProspect = useStore((state) => state.clearSelectedProspect);

  // Importer status is derived from the /me role.marketType rather than Auth0 JWT role
  // codes, which may not match when Auth0 roles are renamed. Mirrors LayoutWrapper and
  // AvailableVehicles so the importer read-only view and CTA gating stay consistent.
  const { userIdentity } = useUserIdentity();
  const isImporter = userIdentity?.role?.marketType === 'Importer';
  // History/Export are importer-only in production, but the feature flag lets all
  // roles exercise them outside production (dev/stage).
  const canViewHistoryExport = isImporter || isHistoryExportEnabledForAllRoles();
  // Importers cannot create prospects; everyone else (agency, admin, unassigned) can.
  const canCreateProspect = !isImporter;

  const { data: listMetadata } = useGetProspectListMetadata();
  const filterOptions = useMemo(
    () => ({
      statuses: toMetadataFilterOptions(listMetadata?.prospectstatus),
      sources: toMetadataFilterOptions(listMetadata?.prospectsource),
      locations: toLocationFilterOptions(listMetadata?.locations),
    }),
    [listMetadata]
  );
  const filterState = useMemo(() => buildFilterState(filters), [filters]);
  const handleFilterChange = useCallback(
    (newFilterState: FilterState) => onFiltersChange?.(parseFilterState(newFilterState)),
    [onFiltersChange]
  );

  const exportProspects = useExportProspects();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [exportStatusMessage, setExportStatusMessage] = useState('');
  const { data: exportHistoryData, isLoading: isHistoryLoading } =
    useGetProspectsExportHistory(isHistoryOpen);

  // Slider is open when there's a selectedProspectId in URL
  const isSliderOpen = !!selectedProspectId;

  const handleEditClick = useCallback(
    (record: Prospect) => {
      // Store prospect info in store for use during uploads (no API call needed)
      // Files are automatically cleared when setting a new prospect
      setSelectedProspect({
        prospectIdentifier: record.prospectIdentifier || '',
        prospectName: `${record.prospectFirstName || ''} ${record.prospectLastName || ''}`.trim(),
        prospectEmail: record.prospectEmail,
        uploadedFiles: [],
      });
      onProspectSelect?.(record.prospectIdentifier || null);
    },
    [onProspectSelect, setSelectedProspect]
  );

  const handleClose = useCallback(() => {
    clearSelectedProspect(); // This now also clears uploaded files
    onProspectSelect?.(null);
  }, [onProspectSelect, clearSelectedProspect]);

  const handleExportSuccess = useCallback(
    (response: ExportProspectsResponse) => {
      // No fileId means the export API found nothing to export (e.g. code "204")
      if (response?.fileId) return;

      setExportStatusMessage(
        response?.message ??
          labels?.['table.exportNoProspectsFound'] ??
          'No new prospects found for given criteria'
      );
    },
    [labels]
  );

  const handleExport = useCallback(() => {
    setExportStatusMessage('');
    exportProspects.mutate(
      { exportType: PROSPECTS_EXPORT_TYPE_INCREMENTAL },
      { onSuccess: handleExportSuccess }
    );
  }, [exportProspects, handleExportSuccess]);

  const handleExportAll = useCallback(() => {
    setExportStatusMessage('');
    exportProspects.mutate(
      { exportType: PROSPECTS_EXPORT_TYPE_ALL },
      { onSuccess: handleExportSuccess }
    );
  }, [exportProspects, handleExportSuccess]);

  const handleExportSearch = useCallback(() => {
    setExportStatusMessage('');
    exportProspects.mutate(
      {
        exportType: PROSPECTS_EXPORT_TYPE_SEARCH,
        prospectSearchRequest: {
          searchString: searchQuery,
          sortField: sortColumn ?? '',
          sortOrder: sortDirection ?? 'desc',
          ...filters,
        },
      },
      { onSuccess: handleExportSuccess }
    );
  }, [exportProspects, handleExportSuccess, searchQuery, sortColumn, sortDirection, filters]);

  const handleExportSelect = useCallback(
    (value: string) => {
      if (value === EXPORT_MODE_ALL) {
        handleExportAll();
        return;
      }

      if (value === EXPORT_MODE_SEARCH) {
        handleExportSearch();
        return;
      }

      handleExport();
    },
    [handleExport, handleExportAll, handleExportSearch]
  );

  const handleHistoryDownload = useCallback(async (fileId: string, fileNameOriginal?: string) => {
    setDownloadingFileId(fileId);

    try {
      await downloadFileFromFilehub(
        fileId,
        fileNameOriginal ?? fileId,
        FILEHUB_MODULE_NAME.PROSPECT_MANAGEMENT
      );
    } finally {
      setDownloadingFileId(null);
    }
  }, []);

  const historyLogs = useMemo<ExportHistoryLog[]>(
    () =>
      (exportHistoryData?.items ?? []).map((item) => ({
        id: item.fileId,
        fileNameOriginal: item.fileNameOriginal,
        exportedAt: item.createdAt,
        exporterName: item.createdBy ?? '--',
      })),
    [exportHistoryData]
  );
  const columns = useMemo(
    () =>
      createColumns({
        theme,
        onEditClick: handleEditClick,
        labels,
        locale,
        isImporter,
        filterOptions,
      }),
    [theme, labels, handleEditClick, locale, isImporter, filterOptions]
  );

  const exportOptions = useMemo(
    () => [
      isImporter
        ? {
            value: EXPORT_MODE_INCREMENTAL,
            label: labels?.['table.exportIncremental'] ?? 'Incremental',
          }
        : {
            value: EXPORT_MODE_SEARCH,
            label: labels?.['table.exportSearch'] ?? 'Search',
          },
      {
        value: EXPORT_MODE_ALL,
        label: labels?.['table.exportAll'] ?? 'All',
      },
    ],
    [labels, isImporter]
  );

  return (
    <>
      {exportStatusMessage && (
        <StatusBanner
          type="info"
          message={exportStatusMessage}
          dataTestId="export-no-prospects-message"
          className={styles.statusBanner}
        />
      )}
      <TableComponent
        className={styles.tableWrapper}
        columns={columns}
        data={data}
        theme={theme === 'light' ? 'dark' : 'light'}
        selection={{ enabled: false }}
        pagination={{
          enabled: true,
          total: totalCount,
          current: currentPage,
          pageSize,
          onChange: (page) => onPageChange(page),
          onPageSizeChange,
        }}
        search={{
          placeholder:
            labels?.['table.searchPlaceholder'] ?? 'Search by name, email, phone, source...',
          value: searchQuery,
          onChange: onSearchChange,
        }}
        sort={{
          column: sortColumn,
          direction: sortDirection,
          onSort: onSort,
        }}
        filter={{
          enabled: true,
          state: filterState,
          onChange: handleFilterChange,
        }}
        columnConfig={{ enabled: true }}
        toolbar={{
          content: (
            <>
              {canCreateProspect && (
                <Button
                  type="secondary"
                  color="neutral"
                  size="small"
                  theme="light"
                  label={labels?.['table.newProspect'] ?? 'NEW PROSPECT'}
                  iconLeft={{ category: 'global', name: 'plus' }}
                  handleClick={onCreateProspect}
                  gaComponent="prospects-new-prospect"
                  dataTestId="new-prospect-button"
                />
              )}
              <Button
                type="secondary"
                color="neutral"
                size="small"
                theme="light"
                label={labels?.['table.testDriveBooking'] ?? 'TEST DRIVE BOOKING'}
                iconLeft={{ category: 'global', name: 'calendar' }}
                handleClick={onViewAllTestDrives}
                gaComponent="prospects-view-all-test-drives"
                dataTestId="view-all-test-drives-button"
              />
            </>
          ),
          panels: canViewHistoryExport
            ? [
                {
                  id: 'export',
                  label: labels?.['table.export'] ?? 'Export',
                  icon: { category: 'global', name: 'download' },
                  content: (
                    <Box className={styles.exportPanel}>
                      <Button
                        type="secondary"
                        color="neutral"
                        size="small"
                        theme="light"
                        isFluid
                        label={labels?.['table.history'] ?? 'HISTORY'}
                        iconLeft={{ category: 'global', name: 'queue' }}
                        handleClick={() => setIsHistoryOpen(true)}
                        gaComponent="prospects-history"
                        dataTestId="history-prospects-button"
                      />
                      <Box data-testid="export-prospects-dropdown">
                        {exportProspects.isPending ? (
                          <Box
                            className={styles.exportPending}
                            data-testid="export-prospects-pending"
                          >
                            <Typography variant="label" size="small" theme="light" color="gray-5">
                              {labels?.['table.exporting'] ?? 'Exporting...'}
                            </Typography>
                          </Box>
                        ) : (
                          <Dropdown
                            theme="light"
                            size="small"
                            name="prospects-export"
                            placeholder={labels?.['table.export'] ?? 'Export'}
                            options={exportOptions}
                            handleSelect={handleExportSelect}
                            gaComponent="prospects-export"
                            showValidation={false}
                          />
                        )}
                      </Box>
                    </Box>
                  ),
                },
              ]
            : undefined,
        }}
        emptyState={{
          message: searchQuery.trim()
            ? (labels?.['table.noResultsFound'] ?? 'No results found! Please update your query')
            : (labels?.['table.noProspectsAvailable'] ?? 'No prospects available'),
        }}
        labels={{
          manageColumns: labels?.['configurator.table.manageColumns'] ?? 'Manage Columns',
          filter: labels?.['configurator.table.filter'] ?? 'Filter',
          filterWithCount: labels?.['configurator.table.filterWithCount'] ?? 'Filter ({{count}})',
          searchPlaceholder:
            labels?.['table.searchPlaceholder'] ?? 'Search by name, email, phone, source...',
          showingItems:
            labels?.['configurator.table.showingItems'] ??
            'Showing {{start}}-{{end}} of {{total}} Items',
          pageOf: labels?.['configurator.table.pageOf'] ?? '{{current}} of {{total}}',
          selectAll: labels?.['configurator.table.selectAll'] ?? 'Select All',
          clearAll: labels?.['configurator.table.clearAll'] ?? 'Clear All',
          columnsVisible:
            labels?.['configurator.table.columnsVisible'] ??
            '{{visible}} of {{total}} columns visible',
          save: labels?.['configurator.table.save'] ?? 'Save',
          apply: labels?.['configurator.table.apply'] ?? 'Apply',
          filtersTitle: labels?.['configurator.table.filtersTitle'] ?? 'Filters',
        }}
        gaComponent="prospects-pipeline-table"
        dataTestId="prospects-pipeline-table"
      />
      <Details
        theme={theme}
        isOpen={isSliderOpen}
        onClose={handleClose}
        prospectId={selectedProspectId || null}
        labels={labels}
        onCreateQuotation={() => {
          onProspectSelect?.(null);
          onCreateQuotation(selectedProspectId || undefined);
        }}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onQuoteClick={onQuoteClick}
      />
      <ExportHistory
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        labels={labels}
        theme={theme}
        logs={historyLogs}
        isLoading={isHistoryLoading}
        isDownloading={!!downloadingFileId}
        downloadingFileId={downloadingFileId}
        onDownload={handleHistoryDownload}
      />
    </>
  );
};

export default Table;
